/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';

import HealthEntryIcons from '../app/components/icons/HealthEntryIcons';
import HealthEntryCategoryNames, { AllergySubType } from '../constants/HealthEntryCategoryNames';
import { PhraseData } from '../utilities/localizedUtil';
import ShareCardIncludedTypeNames from '../constants/ShareCardIncludedTypeNames';

interface HealthEntryCategory {
  kind: HealthEntryCategoryNames;
  latestItemsOnly?: boolean; // only latest items included in the group
  includedValues: ShareCardIncludedTypeNames; // Control rendering behavior
  allowIncludedValuesChange: boolean;
  subType?: AllergySubType;
  identifier: string;
  icon(props): React.ReactFragment;
  getTitlePhraseData(intl): PhraseData;
  getTitleShowHidden(intl, count): PhraseData;
  getTitleHideRevealed(intl, count): PhraseData;
}

function formatOlderCategoryTitle(showOlder, intl, count, phraseId) {
  const string1 = intl.formatMessage({ id: phraseId }, { digit1: count }).toLowerCase();
  return {
    id: showOlder ? 'bpAll.showOlder.button.label' : 'bpAll.hideOlder.button.label',
    values: { string1 },
  };
}
export const healthEntryCategories: HealthEntryCategory[] = [
  {
    kind: HealthEntryCategoryNames.allergy,
    includedValues: ShareCardIncludedTypeNames.latest,
    allowIncludedValuesChange: false,
    identifier: `${HealthEntryCategoryNames.allergy}-${AllergySubType.medication}`,
    subType: AllergySubType.medication,
    icon: (iconProps) => <HealthEntryIcons.IconAllergy {...iconProps} />,
    getTitlePhraseData: (intl) => ({ id: 'list.healthEntry.allergiesMedication.name', values: { digit1: 2 } }),
    getTitleShowHidden: (intl, count) => {
      return formatOlderCategoryTitle(false, intl, count, 'list.healthEntry.allergiesMedication.name');
    },
    getTitleHideRevealed: (intl, count) => {
      return formatOlderCategoryTitle(true, intl, count, 'list.healthEntry.allergiesMedication.name');
    },
  },
  {
    kind: HealthEntryCategoryNames.allergy,
    includedValues: ShareCardIncludedTypeNames.latest,
    allowIncludedValuesChange: false,
    identifier: `${HealthEntryCategoryNames.allergy}-${AllergySubType.food}`,
    subType: AllergySubType.food,
    icon: (iconProps) => <HealthEntryIcons.IconAllergy {...iconProps} />,
    getTitlePhraseData: (intl) => ({ id: 'list.healthEntry.allergiesFood.name', values: { digit1: 2 } }),
    getTitleShowHidden: (intl, count) => {
      return formatOlderCategoryTitle(false, intl, count, 'list.healthEntry.allergiesFood.name');
    },
    getTitleHideRevealed: (intl, count) => {
      return formatOlderCategoryTitle(true, intl, count, 'list.healthEntry.allergiesFood.name');
    },
  },
  {
    kind: HealthEntryCategoryNames.allergy,
    includedValues: ShareCardIncludedTypeNames.latest,
    allowIncludedValuesChange: false,
    identifier: `${HealthEntryCategoryNames.allergy}-${AllergySubType.other}`,
    subType: AllergySubType.other,
    icon: (iconProps) => <HealthEntryIcons.IconAllergy {...iconProps} />,
    getTitlePhraseData: (intl) => ({ id: 'list.healthEntry.allergiesOther.name', values: { digit1: 2 } }),
    getTitleShowHidden: (intl, count) => {
      return formatOlderCategoryTitle(false, intl, count, 'list.healthEntry.allergiesOther.name');
    },
    getTitleHideRevealed: (intl, count) => {
      return formatOlderCategoryTitle(true, intl, count, 'list.healthEntry.allergiesOther.name');
    },
  },
  {
    kind: HealthEntryCategoryNames.condition,
    includedValues: ShareCardIncludedTypeNames.latest,
    allowIncludedValuesChange: false,
    identifier: HealthEntryCategoryNames.condition,
    icon: (iconProps) => <HealthEntryIcons.IconCondition {...iconProps} />,
    getTitlePhraseData: (intl) => ({ id: 'list.healthEntry.conditions.name', values: { digit1: 2 } }),
    getTitleShowHidden: (intl, count) => {
      return formatOlderCategoryTitle(false, intl, count, 'list.healthEntry.conditions.name');
    },
    getTitleHideRevealed: (intl, count) => {
      return formatOlderCategoryTitle(true, intl, count, 'list.healthEntry.conditions.name');
    },
  },
  {
    kind: HealthEntryCategoryNames.medication,
    includedValues: ShareCardIncludedTypeNames.latest,
    allowIncludedValuesChange: false,
    identifier: HealthEntryCategoryNames.medication,
    icon: (iconProps) => <HealthEntryIcons.IconMedication {...iconProps} />,
    getTitlePhraseData: (intl) => ({ id: 'list.healthEntry.medications.name', values: { digit1: 2 } }),
    getTitleShowHidden: (intl, count) => {
      return formatOlderCategoryTitle(false, intl, count, 'list.healthEntry.medications.name');
    },
    getTitleHideRevealed: (intl, count) => {
      return formatOlderCategoryTitle(true, intl, count, 'list.healthEntry.medications.name');
    },
  },
  {
    kind: HealthEntryCategoryNames.otherTreatment,
    includedValues: ShareCardIncludedTypeNames.latest,
    allowIncludedValuesChange: false,
    identifier: HealthEntryCategoryNames.otherTreatment,
    icon: (iconProps) => <HealthEntryIcons.IconTreatment {...iconProps} />,
    getTitlePhraseData: () => ({ id: 'list.healthEntry.treatments.name', values: { digit1: 2 } }),
    getTitleShowHidden: (intl, count) => {
      return formatOlderCategoryTitle(false, intl, count, 'list.healthEntry.treatments.name');
    },
    getTitleHideRevealed: (intl, count) => {
      return formatOlderCategoryTitle(true, intl, count, 'list.healthEntry.treatments.name');
    },
  },
  {
    kind: HealthEntryCategoryNames.procedure,
    includedValues: ShareCardIncludedTypeNames.latest,
    allowIncludedValuesChange: false,
    identifier: HealthEntryCategoryNames.procedure,
    icon: (iconProps) => <HealthEntryIcons.IconProcedure {...iconProps} />,
    getTitlePhraseData: (intl) => ({ id: 'list.healthEntry.procedures.name', values: { digit1: 2 } }),
    getTitleShowHidden: (intl, count) => {
      return formatOlderCategoryTitle(false, intl, count, 'list.healthEntry.procedures.name');
    },
    getTitleHideRevealed: (intl, count) => {
      return formatOlderCategoryTitle(true, intl, count, 'list.healthEntry.procedures.name');
    },
  },
  {
    kind: HealthEntryCategoryNames.vaccination,
    includedValues: ShareCardIncludedTypeNames.latest,
    allowIncludedValuesChange: false,
    identifier: HealthEntryCategoryNames.vaccination,
    icon: (iconProps) => <HealthEntryIcons.IconVaccination {...iconProps} />,
    getTitlePhraseData: (intl) => ({ id: 'list.healthEntry.vaccinations.name', values: { digit1: 2 } }),
    getTitleShowHidden: (intl, count) => {
      return formatOlderCategoryTitle(false, intl, count, 'list.healthEntry.vaccinations.name');
    },
    getTitleHideRevealed: (intl, count) => {
      return formatOlderCategoryTitle(true, intl, count, 'list.healthEntry.vaccinations.name');
    },
  },
  {
    kind: HealthEntryCategoryNames.measurement,
    includedValues: ShareCardIncludedTypeNames.latest,
    allowIncludedValuesChange: true,
    identifier: HealthEntryCategoryNames.measurement,
    icon: (iconProps) => <HealthEntryIcons.IconMeasurement {...iconProps} />,
    getTitlePhraseData: (intl) => {
      return { id: 'list.healthEntry.measurements.name', values: { digit1: 2 } };
    },
    getTitleShowHidden: (intl, count) => {
      return formatOlderCategoryTitle(false, intl, count, 'list.healthEntry.measurements.name');
    },
    getTitleHideRevealed: (intl, count) => {
      return formatOlderCategoryTitle(true, intl, count, 'list.healthEntry.measurements.name');
    },
  },
  {
    kind: HealthEntryCategoryNames.lab,
    includedValues: ShareCardIncludedTypeNames.latest,
    allowIncludedValuesChange: true,
    identifier: HealthEntryCategoryNames.lab,
    icon: (iconProps) => <HealthEntryIcons.IconLab {...iconProps} />,
    getTitlePhraseData: (intl) => {
      return { id: 'list.healthEntry.lab.name', values: { digit1: 2 } };
    },
    getTitleShowHidden: (intl, count) => {
      return formatOlderCategoryTitle(false, intl, count, 'list.healthEntry.lab.name');
    },
    getTitleHideRevealed: (intl, count) => {
      return formatOlderCategoryTitle(true, intl, count, 'list.healthEntry.lab.name');
    },
  },
  {
    kind: HealthEntryCategoryNames.geneticResult,
    includedValues: ShareCardIncludedTypeNames.latest,
    allowIncludedValuesChange: true,
    identifier: HealthEntryCategoryNames.geneticResult,
    icon: (iconProps) => <HealthEntryIcons.IconGeneticResult {...iconProps} />,
    getTitlePhraseData: (intl) => {
      return { id: 'list.healthEntry.genes.name', values: { digit1: 2 } };
    },
    getTitleShowHidden: (intl, count) => {
      return formatOlderCategoryTitle(false, intl, count, 'list.healthEntry.genes.name');
    },
    getTitleHideRevealed: (intl, count) => {
      return formatOlderCategoryTitle(true, intl, count, 'list.healthEntry.genes.name');
    },
  },
];
